import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Flex, Text } from 'rebass/styled-components';
import toast from 'react-hot-toast';
import { Toggle, Loader, MessageBlock } from '@getro/rombo';
import { useUpdateGeneralSettingsMutation } from 'services/network';
import { loadedSaveGeneralSettings } from '../../actions';
import strings from '../../../../strings';

const MemberNotifications = ({ notifyMembersOutOfSync, isLoading, fetchError }) => {
  const dispatch = useDispatch();
  const [updateSettings, { isLoading: isSaving }] = useUpdateGeneralSettingsMutation();

  const handleToggle = async () => {
    const nextValue = !notifyMembersOutOfSync;
    try {
      await updateSettings({ notifyMembersOutOfSync: nextValue }).unwrap();
      // Keep Redux generalSettings (feeds this toggle) in sync after the save.
      dispatch(loadedSaveGeneralSettings({ values: { notifyMembersOutOfSync: nextValue } }));
      toast('Changes saved successfully', { icon: 'success', id: 'out-of-sync-reminder-settings' });
    } catch (error) {
      toast(strings.genericError, { icon: 'error', id: 'out-of-sync-reminder-settings' });
    }
  };

  return (
    <Flex flexDirection="column" sx={{ gap: '16px' }}>
      <Text
        pt="40px"
        pb="8px"
        fontSize="12px"
        color="text.subtle"
        sx={{ borderBottom: 1, borderBottomColor: 'neutral.100', textTransform: 'uppercase', letterSpacing: '1px' }}
      >
        Member notifications
      </Text>
      {fetchError && <MessageBlock type="error">{strings.genericError}</MessageBlock>}
      {!fetchError && isLoading && <Loader size="small" />}
      {!fetchError && !isLoading && (
        <Flex alignItems="center" justifyContent="space-between" sx={{ gap: '16px' }}>
          <Flex sx={{ flexDirection: 'column', rowGap: '4px' }}>
            <Text lineHeight="140%" fontSize="14px" fontWeight="500" as="h4">
              Remind members to reconnect LinkedIn
            </Text>
            <Text as="p" color="text.main" lineHeight="20px" fontSize="14px">
              Email members whose LinkedIn contacts haven&apos;t synced in a while, asking them to reconnect. Members
              can opt out from their own notification settings.
            </Text>
          </Flex>
          <Toggle size="large" checked={notifyMembersOutOfSync} disabled={isSaving} onChange={handleToggle} />
        </Flex>
      )}
    </Flex>
  );
};

MemberNotifications.propTypes = {
  notifyMembersOutOfSync: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  fetchError: PropTypes.bool.isRequired,
};

export default MemberNotifications;
